/**
 * @file category-list.ts
 *
 * List-domain helpers for the Categories tab: the page size and the pure
 * function that orders the flat category list into indented, tree-ordered rows.
 * Kept separate from the screen so the ordering logic is unit-testable without
 * rendering, mirroring the products tab's `product-list.ts`.
 */

import type { ShopCategory } from '../api/shop-types';
import { buildCategoryTree } from './build-category-tree';

/** Categories shown per page in the list view. */
export const CATEGORIES_PAGE_SIZE = 20;

/** A category paired with its tree-nesting depth, for an indented row. */
export interface CategoryRow {
  category: ShopCategory;
  depth: number;
}

/**
 * Orders the flat category list as a tree (children after their parent, with a
 * depth for indentation). Any category not reachable from a root (orphaned by
 * malformed data) is appended at depth 0 so no row is ever hidden.
 *
 * @param categories - The full flat category list.
 *
 * @returns The categories ordered for display, each with its depth.
 */
export function buildCategoryRows(categories: ShopCategory[]): CategoryRow[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const nodes = buildCategoryTree(categories);
  const seen = new Set(nodes.map((n) => n.id));
  const rows: CategoryRow[] = [];
  for (const node of nodes) {
    const category = byId.get(node.id);
    if (category) {
      rows.push({ category, depth: node.depth });
    }
  }
  for (const category of categories) {
    if (!seen.has(category.id)) {
      rows.push({ category, depth: 0 });
    }
  }
  return rows;
}
